import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

interface FormPageProps {
  title: string;
  children: React.ReactNode;
  onSubmit: () => void;
  submitLabel?: string;
  saving?: boolean;
  submitDisabled?: boolean;
  /** Есть несохранённые изменения: подтверждение при уходе */
  dirty?: boolean;
  /** Куда уйти по «✕»; по умолчанию — назад */
  closeTo?: string;
  /** Доп. кнопка слева от «Сохранить» (например, «Назад» в мастере) */
  secondary?: React.ReactNode;
  /** Справа в заголовке (например, ⋮ с «Удалить») */
  headerActions?: React.ReactNode;
}

/**
 * Страница формы. Mobile: fullscreen, «✕» + заголовок, sticky «Сохранить» снизу.
 * Desktop: страница шириной ≤720px. Роуты форм рендерятся в AppLayout с `bare`.
 */
export const FormPage: React.FC<FormPageProps> = ({
  title,
  children,
  onSubmit,
  submitLabel = 'Сохранить',
  saving = false,
  submitDisabled = false,
  dirty = false,
  closeTo,
  secondary,
  headerActions,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const close = () => {
    if (dirty && !window.confirm('Есть несохранённые изменения. Выйти без сохранения?')) return;
    if (closeTo) navigate(closeTo);
    else navigate(-1);
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      sx={{ maxWidth: 720, mx: 'auto', minHeight: { xs: '100dvh', lg: 'auto' }, display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 0, lg: 56 },
          zIndex: 2,
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          pt: 'env(safe-area-inset-top)',
          minHeight: 56,
        }}
      >
        <IconButton aria-label="Закрыть" onClick={close} sx={{ width: 48, height: 48 }}>
          <Close />
        </IconButton>
        <Typography variant="h3" component="h1" noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        {headerActions}
      </Box>
      <Box sx={{ flex: 1, px: 2, pt: 1, pb: 3 }}>{children}</Box>
      <Paper
        square
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          display: 'flex',
          gap: 1,
          p: 2,
          pb: 'calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        {secondary}
        <Button
          type="submit"
          variant="contained"
          color="manage"
          size="large"
          fullWidth
          disabled={saving || submitDisabled}
          sx={{ minHeight: 48 }}
        >
          {saving ? 'Сохранение…' : submitLabel}
        </Button>
      </Paper>
    </Box>
  );
};
